import { Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import TesSalaryTablePickerDialog from "../../tesSalaryTables/TesSalaryTablePickerDialog";
import TesSalaryTableSelectionSummary from "../../tesSalaryTables/TesSalaryTableSelectionSummary";
import {
  getSalaryAmountFromCell,
  getTesSalaryTableCell,
} from "../../tesSalaryTables/helpers";
import { SalaryType } from "../../orders/types/salaryType";
import {
  TesSalaryTableDocument,
  TesSalaryTableSelectionProvenance,
} from "../../tesSalaryTables/tesSalaryTablesApi";
import {
  fetchActiveTesSalaryTablesThunk,
  fetchTesSalaryTableByIdThunk,
} from "../../tesSalaryTables/tesSalaryTablesSlice";

type Props = {
  disabled?: boolean;
  tesId?: string | null;
  workTitleId?: string | null;
  date?: string | null;
  salaryType?: SalaryType;
  hasZones: boolean;
  showLabel?: boolean;
  value?: TesSalaryTableSelectionProvenance | null;
  onChange: (payload: {
    selection: TesSalaryTableSelectionProvenance;
    table: TesSalaryTableDocument | null;
    salaryAmount: string;
  }) => void;
  onAvailabilityChange?: (isAvailable: boolean) => void;
  onSelectionUnavailable?: () => void;
};

const EmploymentTesSalaryTableField = ({
  disabled,
  tesId,
  workTitleId,
  date,
  salaryType = SalaryType.Hourly,
  hasZones,
  showLabel = true,
  value,
  onChange,
  onAvailabilityChange,
  onSelectionUnavailable,
}: Props) => {
  const { t } = useTranslation("settings");
  const dispatch = useAppDispatch();
  const tesLabelsById = useAppSelector((state) => state.localized.tes);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [activeTables, setActiveTables] = useState<TesSalaryTableDocument[]>(
    [],
  );
  const [tesSalaryTablesEnabled, setTesSalaryTablesEnabled] = useState(false);
  const [selectedTable, setSelectedTable] =
    useState<TesSalaryTableDocument | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onAvailabilityChangeRef = useRef(onAvailabilityChange);
  onAvailabilityChangeRef.current = onAvailabilityChange;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSelectionUnavailableRef = useRef(onSelectionUnavailable);
  onSelectionUnavailableRef.current = onSelectionUnavailable;
  const previousSalaryTypeRef = useRef(salaryType);
  const lastNotifiedAvailabilityRef = useRef<boolean | undefined>(undefined);

  const notifyAvailabilityChange = useCallback((isAvailable: boolean) => {
    if (lastNotifiedAvailabilityRef.current === isAvailable) return;
    lastNotifiedAvailabilityRef.current = isAvailable;
    onAvailabilityChangeRef.current?.(isAvailable);
  }, []);

  useEffect(() => {
    if (previousSalaryTypeRef.current !== salaryType) {
      lastNotifiedAvailabilityRef.current = undefined;
    }

    if (!tesId || !workTitleId || !date) {
      setActiveTables([]);
      setTesSalaryTablesEnabled(false);
      setSelectedTable(null);
      notifyAvailabilityChange(false);
      return;
    }

    // Guard against out-of-order responses when TES/work title/date changes
    // quickly; only the latest request is allowed to update local state.
    let isCancelled = false;

    dispatch(
      fetchActiveTesSalaryTablesThunk({
        tesId,
        workTitleId,
        date,
        salaryType,
      }),
    )
      .unwrap()
      .then((result) => {
        if (isCancelled) return;
        const currentValue = valueRef.current;
        const tesSalaryTableId = currentValue?.tesSalaryTableId;
        setTesSalaryTablesEnabled(result.enabled);
        setActiveTables(result.tables);
        if (result.tables.length === 0) {
          setSelectedTable(null);
        }
        if (tesSalaryTableId) {
          const existingTable = result.tables.find(
            (table) => table.tesSalaryTableId === tesSalaryTableId,
          );
          if (existingTable) {
            setSelectedTable(existingTable);
            if (previousSalaryTypeRef.current !== salaryType && currentValue) {
              const cell = getTesSalaryTableCell(
                existingTable,
                currentValue.rowId,
                currentValue.columnId,
              );
              const salaryAmount = getSalaryAmountFromCell(cell, salaryType);
              if (salaryAmount) {
                onChangeRef.current({
                  selection: currentValue,
                  table: existingTable,
                  salaryAmount,
                });
              } else {
                onSelectionUnavailableRef.current?.();
              }
            }
          } else {
            onSelectionUnavailableRef.current?.();
            dispatch(fetchTesSalaryTableByIdThunk(tesSalaryTableId))
              .unwrap()
              .then((table) => {
                if (isCancelled) return;
                setSelectedTable(table || null);
              })
              .catch(() => {
                if (isCancelled) return;
                setSelectedTable(null);
              });
          }
        }
        previousSalaryTypeRef.current = salaryType;
        notifyAvailabilityChange(result.enabled && result.tables.length > 0);
      })
      .catch(() => {
        if (isCancelled) return;
        setTesSalaryTablesEnabled(false);
        setActiveTables([]);
        setSelectedTable(null);
        previousSalaryTypeRef.current = salaryType;
        notifyAvailabilityChange(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [
    date,
    tesId,
    workTitleId,
    salaryType,
    dispatch,
    notifyAvailabilityChange,
  ]);

  const pickerTables = activeTables;

  const hasAvailableTables = tesSalaryTablesEnabled && pickerTables.length > 0;

  if (!hasAvailableTables) {
    return null;
  }

  return (
    <>
      {showLabel && tesSalaryTablesEnabled ? (
        <>
          <Grid item xs={4} textAlign="end" alignSelf="center">
            <Typography variant="secondary">
              {t("tesSalaryTablesTitle")}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            <TesSalaryTableSelectionSummary
              disabled={disabled}
              hasTables={hasAvailableTables}
              isUnavailableForZones={hasAvailableTables && hasZones}
              onOpenPicker={() => setIsPickerOpen(true)}
              selection={value}
              selectedTable={selectedTable}
            />
          </Grid>
        </>
      ) : (
        <TesSalaryTableSelectionSummary
          disabled={disabled}
          hasTables={hasAvailableTables}
          isUnavailableForZones={hasAvailableTables && hasZones}
          onOpenPicker={() => setIsPickerOpen(true)}
          selection={value}
          selectedTable={selectedTable}
        />
      )}
      <DialogComponent
        autoWidth
        open={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
      >
        <TesSalaryTablePickerDialog
          tables={pickerTables}
          salaryType={salaryType}
          initialSelection={value}
          tesLabelsById={tesLabelsById}
          onCancel={() => setIsPickerOpen(false)}
          onConfirm={(selection) => {
            const table =
              pickerTables.find(
                (item) => item.tesSalaryTableId === selection.tesSalaryTableId,
              ) || null;

            setSelectedTable(table);
            onChange({
              selection: {
                tesSalaryTableId: selection.tesSalaryTableId,
                rowId: selection.rowId,
                columnId: selection.columnId,
                mode: "FULL_SALARY",
              },
              table,
              salaryAmount: selection.salaryAmount,
            });
            setIsPickerOpen(false);
          }}
        />
      </DialogComponent>
    </>
  );
};

export default EmploymentTesSalaryTableField;
